import { t } from "@travily/i18n";
import { profilePath, programPath } from "@travily/routes";
import type Program from "@travily/types/models/ProgramsCard";
import type {
  ProgramsCardAccountTrait,
  ProgramsCardFavoriteTrait,
  ProgramsCardPhotosTrait,
} from "@travily/types/models/ProgramsCard";
import { CardTag } from "@ui/components/Card";
import type { CardTheme } from "@ui/types/card";
import { ListingCard, type ListingCardAuthor } from "@/components/ListingCard/ListingCard";

function FeaturedCheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-[18px] shrink-0"
      fill="none"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9" cy="9" fill="#84A821" r="9" />
      <path
        d="M5.4508 9.92271L7.07523 11.996L12.9589 7.08197"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

export type ProgramCardProgram = Program &
  ProgramsCardPhotosTrait &
  Partial<ProgramsCardAccountTrait & ProgramsCardFavoriteTrait>;

function resolveAuthor(program: ProgramCardProgram): ListingCardAuthor | undefined {
  const { account } = program;
  if (!account?.handle) return undefined;

  return {
    profile_type: account.profile_type,
    avatar: account.avatar,
    href: profilePath(account.handle),
    initial: account.initial ?? "",
    name: account.display_name ?? account.handle,
  };
}

export interface ProgramCardProps {
  className?: string;
  favorited?: boolean;
  onFavoriteToggle?: () => void;
  program: ProgramCardProgram;
  showAuthor?: boolean;
  theme?: CardTheme;
}

export function ProgramCard({ className, favorited, onFavoriteToggle, program, showAuthor, theme }: ProgramCardProps) {
  const photoImages = program.photos.map((photo) => photo.image).filter((image) => image != null);
  const cover = photoImages[0];
  const title = program.name;
  const author = showAuthor ? resolveAuthor(program) : undefined;
  const featuredTag = program.featured ? (
    <CardTag icon={<FeaturedCheckIcon />}>{t("components.card.featured")}</CardTag>
  ) : undefined;

  return (
    <ListingCard
      author={author}
      className={className}
      description={program.tagline ?? null}
      favorited={favorited}
      href={programPath(program.id)}
      image={cover}
      images={photoImages.length > 1 ? photoImages : undefined}
      linkLabel={title}
      onFavoriteToggle={onFavoriteToggle}
      overlayTopLeft={featuredTag}
      theme={theme}
      title={title}
    />
  );
}
